import React, { useCallback, useEffect, useRef, useState } from 'react'
import mapboxgl from 'mapbox-gl'
import BaseMap from './BaseMap'
import RadiusCircle from './RadiusCircle'
import { theme } from '../../config/theme'

const ShowEventMap = ({ 
  className = '',
  eventLocation,
  radius,
  sales = [],
  onSaleSelect
}) => {
  const eventMarker = useRef(null)
  const salesMarkers = useRef([])
  const [mapInstance, setMapInstance] = useState(null)

  // Handle map ready
  const handleMapReady = useCallback((mapInstance) => {
    setMapInstance(mapInstance)
  }, [])

  // Initialize event marker when map is ready
  useEffect(() => {
    if (!mapInstance || !eventLocation) return

    const { latitude, longitude } = eventLocation
    const lat = parseFloat(latitude)
    const lng = parseFloat(longitude)

    if (!isNaN(lat) && !isNaN(lng)) {
      // Create event marker (fixed position)
      if (eventMarker.current) {
        eventMarker.current.remove()
      }
      
      eventMarker.current = new mapboxgl.Marker({ color: theme.colors.primary[500] })
        .setLngLat([lng, lat])
        .addTo(mapInstance)

      // Don't center here - let the sales effect handle bounds fitting
    }
  }, [mapInstance, eventLocation])

  // Update sales markers when sales data changes
  useEffect(() => {
    if (!mapInstance) return

    // Remove existing sales markers
    salesMarkers.current.forEach(marker => marker.remove())
    salesMarkers.current = []

    if (!sales.length) {
      // No sales - just fit to event radius if we have event location
      if (eventLocation && radius) {
        const eventLng = parseFloat(eventLocation.longitude)
        const eventLat = parseFloat(eventLocation.latitude)
        const radiusInDegrees = radius / 69 // Convert miles to degrees
        
        const bounds = [
          [eventLng - radiusInDegrees, eventLat - radiusInDegrees],
          [eventLng + radiusInDegrees, eventLat + radiusInDegrees]
        ]
        
        mapInstance.fitBounds(bounds, {
          padding: 50,
          maxZoom: 15
        })
      }
      return
    }

    // Add sales markers
    const saleCoordinates = []
    sales.forEach(sale => {
      if (!sale.location?.latitude || !sale.location?.longitude) return

      const lng = parseFloat(sale.location.longitude)
      const lat = parseFloat(sale.location.latitude)
      
      if (!isNaN(lng) && !isNaN(lat)) {
        saleCoordinates.push([lng, lat])
        
        const marker = new mapboxgl.Marker({ color: theme.colors.red[500] })
          .setLngLat([lng, lat])
          .addTo(mapInstance)

        // Add click handler to marker element
        marker.getElement().addEventListener('click', () => {
          if (onSaleSelect) {
            onSaleSelect(sale)
          }
        })

        salesMarkers.current.push(marker)
      }
    })

    // Calculate bounds to include event center and all sales
    if (eventLocation && saleCoordinates.length > 0) {
      const eventLng = parseFloat(eventLocation.longitude)
      const eventLat = parseFloat(eventLocation.latitude)
      const radiusInDegrees = radius / 69 // Convert miles to degrees
      
      // Calculate radius bounds (minimum area that should always be visible)
      const radiusBounds = [
        [eventLng - radiusInDegrees, eventLat - radiusInDegrees],
        [eventLng + radiusInDegrees, eventLat + radiusInDegrees]
      ]
      
      // Calculate sales bounds
      const allCoordinates = [[eventLng, eventLat], ...saleCoordinates]
      const lngs = allCoordinates.map(coord => coord[0])
      const lats = allCoordinates.map(coord => coord[1])
      
      const salesBounds = [
        [Math.min(...lngs), Math.min(...lats)], // Southwest corner
        [Math.max(...lngs), Math.max(...lats)]  // Northeast corner
      ]
      
      // Use the larger of the two bounds (radius bounds or sales bounds)
      const finalBounds = [
        [
          Math.min(radiusBounds[0][0], salesBounds[0][0]), // Min lng
          Math.min(radiusBounds[0][1], salesBounds[0][1])  // Min lat
        ],
        [
          Math.max(radiusBounds[1][0], salesBounds[1][0]), // Max lng
          Math.max(radiusBounds[1][1], salesBounds[1][1])  // Max lat
        ]
      ]
      
      // Fit bounds - this ensures we never zoom closer than the radius allows
      mapInstance.fitBounds(finalBounds, {
        padding: 50, // Good visual buffer
        maxZoom: 16  // Additional safety net
      })
    }
  }, [sales, onSaleSelect, mapInstance, eventLocation, radius])

  // Get initial map center
  const initialCenter = eventLocation 
    ? [parseFloat(eventLocation.longitude), parseFloat(eventLocation.latitude)]
    : [-98.5795, 39.8283]

  return (
    <div className={className}>
      <BaseMap
        center={initialCenter}
        zoom={eventLocation ? 12 : 3}
        onMapReady={handleMapReady}
      >
        <RadiusCircle
          center={eventLocation ? [parseFloat(eventLocation.longitude), parseFloat(eventLocation.latitude)] : null}
          radius={radius}
          visible={!!eventLocation}
        />
      </BaseMap>
    </div>
  )
}

export default ShowEventMap